import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const headerStyle = css`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1.5rem;
	flex-wrap: wrap;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex-direction: column;
		align-items: stretch;
	}
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const titleStyle = css`
	${theme.typography.pageTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const descriptionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
	white-space: wrap;
`;

const actionsStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-shrink: 0;
`;

type Props = {
	/** Capitalized monospace section marker, e.g. "ADMINISTRATION · ORGANISATION". */
	readonly eyebrow?: string;
	readonly title: React.ReactNode;
	readonly description?: string;
	/** Right-aligned slot (buttons, links); wraps under the title below `layout.compactBreakpoint`. */
	readonly actions?: React.ReactNode;
};

/**
 * Section marker copied by every screen: eyebrow + title + description, plus a right-aligned actions
 * slot. `design-direction.md` calls it "the marker of every section of the application".
 */
const PageHeader: React.FC<Props> = ({ eyebrow, title, description, actions }) => {
	return (
		<header css={headerStyle}>
			<div css={textStyle}>
				<ConditionalLoader data={eyebrow}>
					<span css={eyebrowStyle}>{eyebrow}</span>
				</ConditionalLoader>
				<h1 css={titleStyle}>{title}</h1>
				{description !== undefined ? <p css={descriptionStyle}>{description}</p> : null}
			</div>
			{actions !== undefined ? <div css={actionsStyle}>{actions}</div> : null}
		</header>
	);
};

export default PageHeader;
